import {
    Button,
    HStack,
    Spacer,
    Box,
    FormErrorMessage,
    FormControl,
    Menu,
    MenuButton,
    IconButton,
    MenuList,
    Heading,
    useDisclosure,
    Link,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'

import { siteName } from '../../HeaderSiteName.js'
import { LogoMenu } from '../../LogoMenu'
import { HamburgerMenu } from './HamburgerMenu'

import { ExtraMenuIcon, NewFacilityIcon, HearingListIcon } from '@/assets/icon'
import { FreewordInput } from '@/components/Elements'
import { InternalUse } from '@/components/Elements/Conditional'
import { FEATURE_HEARING, HEARING_URL, MODE_HAKEN } from '@/config'
import { JobSeekerHeader } from '@/features/jobseekers'
import { setFreeword } from '@/stores'
import { backendApi } from '@/stores/backendApi'

export const HeaderButton = ({ children, onClick }) => (
    <Button
        variant="solid"
        h="56px"
        w="56px"
        textAlign="center"
        fontSize="10px"
        borderRadius="lg"
        onClick={onClick}
    >
        {children}
    </Button>
)
HeaderButton.propTypes = {
    children: PropTypes.node,
    onClick: PropTypes.func,
}

export const MenuListButton = ({ children, onClick }) => (
    <Button
        variant="solid"
        w="200px"
        borderRadius="lg"
        onClick={() => onClick()}
    >
        {children}
    </Button>
)
MenuListButton.propTypes = {
    children: PropTypes.node,
    onClick: PropTypes.func,
}

export const Header = () => {
    const { onOpen, isOpen, onClose } = useDisclosure()
    const location = useLocation()
    const isNewDev = location.pathname.includes('new-development')
    return (
        <>
            <HStack h="69px" px={4} alignItems="center" spacing="10px">
                <LogoMenu />
                <Heading
                    size="lg"
                    color="tryt.primary-1"
                    borderLeftWidth="2px"
                    borderLeftColor="tryt.primary-1"
                    px="10px"
                >
                    <Box as="span" display="block" fontSize="17px">
                        {siteName}
                        {MODE_HAKEN && <Box as="span">×派遣</Box>}
                    </Box>
                    <Box as="span" display="block" fontSize="13px">
                        事業所検索
                    </Box>
                </Heading>
                <Box
                    as="h3"
                    fontSize="15px"
                    color="tryt.primary-1"
                    fontWeight="bolder"
                    pl="60px"
                >
                    施設検索
                </Box>
                <Box w="350px">
                    <FreewordForm />
                </Box>
                <Spacer />
                <HStack spacing={1}>
                    <InternalUse>
                        <IconButton
                            as={Link}
                            aria-label="new-development"
                            icon={<NewFacilityIcon />}
                            rounded="md"
                            variant="ghost"
                            pt={1}
                            pl={1}
                            border="none"
                            width="50px"
                            height="50px"
                            _hover={{
                                bg: '#EAEEFA',
                            }}
                            _active={{
                                bg: '#EAEEFA',
                            }}
                            href="/new-development"
                        />
                    </InternalUse>
                    {FEATURE_HEARING && (
                        <InternalUse>
                            <IconButton
                                as={Link}
                                aria-label="hearing-list"
                                icon={<HearingListIcon />}
                                rounded="md"
                                variant="ghost"
                                pt={1}
                                pl={1}
                                border="none"
                                width="50px"
                                height="50px"
                                _hover={{
                                    bg: '#EAEEFA',
                                }}
                                _active={{
                                    bg: '#EAEEFA',
                                }}
                                href={
                                    HEARING_URL
                                        ? HEARING_URL + '/hearings/'
                                        : '#'
                                }
                                target="_blank"
                            />
                        </InternalUse>
                    )}
                    <Menu isOpen={isOpen} onClose={onClose}>
                        <MenuButton
                            as={IconButton}
                            icon={<ExtraMenuIcon />}
                            rounded="md"
                            variant="ghost"
                            fontSize="24.38px"
                            width="50px"
                            height="50px"
                            _hover={{
                                bg: '#EAEEFA',
                            }}
                            _active={{
                                bg: '#EAEEFA',
                            }}
                            onClick={onOpen}
                        />
                        <MenuList>
                            <HamburgerMenu onClose={onClose} />
                        </MenuList>
                    </Menu>
                </HStack>
            </HStack>
            {/* 求職者 */}
            {!isNewDev && <JobSeekerHeader h="24px" px={4} bg="tryt.gray-2" />}
        </>
    )
}

const FreewordForm = () => {
    const value = useSelector((state) => state.search.freeword)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [writeSearchLog] = backendApi.endpoints.writeSearchLog.useMutation()
    const inputRef = useRef(null)
    return (
        <Box
            as="form"
            m={0}
            onSubmit={(e) => {
                e.preventDefault()
                const trimmedValue = value.trim()
                dispatch(setFreeword(trimmedValue))
                inputRef.current.value = trimmedValue

                writeSearchLog({
                    data: {
                        q: trimmedValue,
                    },
                    msg: 'フリーワード検索が実行されました。',
                    function: 'freeword-search',
                    businessType: MODE_HAKEN ? 'haken' : 'shokai',
                })
                const to =
                    location.pathname.replace(/(.+)\/$/, '$1') ===
                    '/facilities/map'
                        ? '/facilities/map'
                        : '/facilities'
                navigate(to, {
                    state: {
                        mode: 'freewordOnly',
                        isNavigate: true,
                    },
                })
            }}
        >
            <FormControl
                isInvalid={
                    location.state?.error?.payload?.data?.name ===
                        'MongoServerError' &&
                    location.state?.error?.payload?.data?.searchWord === value
                }
            >
                <FreewordInput
                    placeholder="SFID　施設名　法人名"
                    value={value ?? ''}
                    inputRef={inputRef ?? ''}
                    onClear={(e) => {
                        inputRef.current.value = ''
                        dispatch(setFreeword(e.target.value))
                    }}
                    onChange={(e) => {
                        dispatch(setFreeword(e.target.value))
                    }}
                    onBlur={(e) => {
                        const trimmedValue = e.target.value.trim()
                        dispatch(setFreeword(trimmedValue))
                        inputRef.current.value = trimmedValue
                    }}
                />
                <FormErrorMessage>
                    検索時にエラーが発生しました。
                </FormErrorMessage>
            </FormControl>
        </Box>
    )
}
// FreewordForm.whyDidYouRender = true
